import React, { useState, useMemo } from 'react'
import type { SavedBet } from '../context/BetContext'
import {
  decimalToFractional,
  decimalOddsToImpliedProbability,
  calculateReturn,
  formatDecimalOdds,
  formatCurrency,
  calculateValue,
} from '../utils/odds'

interface OddsInputProps {
  selections: SavedBet[]
  onSubmit: (oddsDecimal: number, stake: number) => void
  onCancel: () => void
}

// Calculate combined probability for accumulator (multiply individual probabilities)
function calculateCombinedProbability(selections: SavedBet[]): number {
  if (selections.length === 0) return 0
  const combined = selections.reduce((acc, sel) => acc * (sel.confidencePct / 100), 1)
  return combined * 100
}

export function OddsInput({ selections, onSubmit, onCancel }: OddsInputProps) {
  const [oddsInput, setOddsInput] = useState('')
  const [stakeInput, setStakeInput] = useState('')

  const oddsDecimal = parseFloat(oddsInput) || 0
  const stake = parseFloat(stakeInput) || 0

  const isValid = oddsDecimal > 1 && stake > 0

  const combinedProbability = useMemo(() => calculateCombinedProbability(selections), [selections])
  const impliedProb = oddsDecimal > 1 ? decimalOddsToImpliedProbability(oddsDecimal) : null
  const value = oddsDecimal > 1 ? calculateValue(combinedProbability, oddsDecimal) : null
  const potentialReturn = isValid ? calculateReturn(stake, oddsDecimal) : null
  const profit = isValid ? potentialReturn! - stake : null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isValid) {
      onSubmit(oddsDecimal, stake)
    }
  }

  return (
    <div className="odds-input-overlay" onClick={onCancel}>
      <div className="odds-input-modal" onClick={(e) => e.stopPropagation()}>
        <div className="odds-input-header">
          <h3>Track Bet Slip</h3>
          <button className="odds-input-close" onClick={onCancel}>
            &times;
          </button>
        </div>

        <div className="odds-input-selections">
          <div className="selections-header">
            {selections.length} Selection{selections.length !== 1 ? 's' : ''}
          </div>
          <div className="selections-list">
            {selections.map((sel) => (
              <div key={sel.id} className="selection-item">
                <div className="selection-fixture">{sel.fixture}</div>
                <div className="selection-pick">
                  <span className="selection-title">{sel.title}</span>
                  <span className="selection-line">Over {sel.line}</span>
                  <span className="selection-prob">{sel.confidencePct}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="odds-input-probability">
          <div className="prob-row">
            <span className="prob-label">Combined Probability</span>
            <span className="prob-value">{combinedProbability.toFixed(1)}%</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="odds-input-form">
          <div className="input-group">
            <label htmlFor="odds">Your Odds (Decimal)</label>
            <input
              type="number"
              id="odds"
              value={oddsInput}
              onChange={(e) => setOddsInput(e.target.value)}
              placeholder="e.g. 5.50"
              step="0.01"
              min="1.01"
              autoFocus
            />
            {oddsDecimal > 1 && (
              <div className="input-hint">
                = {decimalToFractional(oddsDecimal)} fractional
              </div>
            )}
          </div>

          <div className="input-group">
            <label htmlFor="stake">Stake</label>
            <div className="stake-input-wrapper">
              <span className="currency-symbol">£</span>
              <input
                type="number"
                id="stake"
                value={stakeInput}
                onChange={(e) => setStakeInput(e.target.value)}
                placeholder="10.00"
                step="0.01"
                min="0.01"
              />
            </div>
          </div>

          {oddsDecimal > 1 && (
            <div className="odds-comparison">
              <div className="comparison-row">
                <span className="comparison-label">Implied Probability</span>
                <span className="comparison-value">{impliedProb?.toFixed(1)}%</span>
              </div>
              {value !== null && (
                <div className="comparison-row">
                  <span className="comparison-label">Value</span>
                  <span className={`comparison-value ${value >= 0 ? 'value-positive' : 'value-negative'}`}>
                    {value >= 0 ? '+' : ''}{value.toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
          )}

          {isValid && (
            <div className="returns-preview">
              <div className="returns-row">
                <span className="returns-label">Potential Returns</span>
                <span className="returns-value">{formatCurrency(potentialReturn!)}</span>
              </div>
              <div className="returns-row profit">
                <span className="returns-label">Profit</span>
                <span className="returns-value">{formatCurrency(profit!)}</span>
              </div>
            </div>
          )}

          <div className="odds-input-actions">
            <button type="button" className="btn-cancel" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn-track" disabled={!isValid}>
              Track Bet
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
